import { DataSourceCode } from "@syla/shared/types/models/DataSourceBase";

export const buildExplorerUrl = ({
  code,
  txHash,
}: {
  txHash: string;
  code?: DataSourceCode;
}) => {
  const escapedHash = encodeURIComponent(txHash);

  // handle empty string ""
  if (!escapedHash) {
    return undefined;
  }

  // without an explicit data source code it's too unreliable to guess
  // which chain the hash belongs to.
  if (!code) {
    return undefined;
  }

  switch (code) {
    case DataSourceCode.Algorand:
      return `https://allo.info/tx/${escapedHash}`;
    case DataSourceCode.Tezos:
      return `https://tzkt.io/${escapedHash}`;
    case DataSourceCode.Cardano:
      return `https://cardanoscan.io/transaction/${escapedHash}`;
    case DataSourceCode.Oraichain:
      return `https://scan.orai.io/txs/${escapedHash}`;
    case DataSourceCode.NEM:
      return `https://explorer.nemtool.com/#/s_tx?hash=${escapedHash}`;
    case DataSourceCode.Monero:
      return `https://monerohash.com/explorer/search?value=${escapedHash}`;
    case DataSourceCode.Aptos:
      return `https://explorer.aptoslabs.com/txn/${escapedHash}?network=mainnet`;
    case DataSourceCode.GateChain:
      return `https://gatescan.org/tx/${escapedHash}`;
    case DataSourceCode.Sui:
      return `https://suiscan.xyz/mainnet/tx/${escapedHash}`;
    case DataSourceCode.Filecoin:
      return `https://filfox.info/en/message/${escapedHash}`;
    case DataSourceCode.XprNetwork:
      return `https://explorer.xprnetwork.org/transaction/${escapedHash}`;
    case DataSourceCode.PIVX:
      return `https://zkbitcoin.com/tx/${escapedHash}`;

    // mintscan urls
    case DataSourceCode.Cosmos:
      return `https://www.mintscan.io/cosmos/tx/${escapedHash}`;
    case DataSourceCode.Akash:
      return `https://www.mintscan.io/akash/tx/${escapedHash}`;
    case DataSourceCode.Injective:
      return `https://www.mintscan.io/injective/tx/${escapedHash}`;
    case DataSourceCode.Agoric:
      return `https://www.mintscan.io/agoric/tx/${escapedHash}`;
    case DataSourceCode.Althea:
      return `https://www.mintscan.io/althea/tx/${escapedHash}`;
    case DataSourceCode.Archway:
      return `https://www.mintscan.io/archway/tx/${escapedHash}`;
    case DataSourceCode.Atomone:
      return `https://www.mintscan.io/atomone/tx/${escapedHash}`;
    case DataSourceCode.Axelar:
      return `https://www.mintscan.io/axelar/tx/${escapedHash}`;
    case DataSourceCode.Band:
      return `https://www.mintscan.io/band/tx/${escapedHash}`;
    case DataSourceCode.Bitsong:
      return `https://www.mintscan.io/bitsong/tx/${escapedHash}`;
    case DataSourceCode.Celestia:
      return `https://www.mintscan.io/celestia/tx/${escapedHash}`;
    case DataSourceCode.Chihuahua:
      return `https://www.mintscan.io/chihuahua/tx/${escapedHash}`;
    case DataSourceCode.Coreum:
      return `https://www.mintscan.io/coreum/tx/${escapedHash}`;
    case DataSourceCode.CronosPos:
      return `https://www.mintscan.io/crypto-org/tx/${escapedHash}`;
    case DataSourceCode.DyDxChain:
      return `https://www.mintscan.io/dydx/tx/${escapedHash}`;
    case DataSourceCode.Evmos:
      return `https://www.mintscan.io/evmos/tx/${escapedHash}`;
    case DataSourceCode.FetchAi:
      return `https://www.mintscan.io/fetchai/tx/${escapedHash}`;
    case DataSourceCode.Finschia:
      return `https://www.mintscan.io/finschia/tx/${escapedHash}`;
    case DataSourceCode.Govgen:
      return `https://www.mintscan.io/govgen/tx/${escapedHash}`;
    case DataSourceCode.GravityBridge:
      return `https://www.mintscan.io/gravity-bridge/tx/${escapedHash}`;
    case DataSourceCode.HumansAi:
      return `https://www.mintscan.io/humans/tx/${escapedHash}`;
    case DataSourceCode.Iris:
      return `https://www.mintscan.io/iris/tx/${escapedHash}`;
    case DataSourceCode.Kava:
      return `https://www.mintscan.io/kava/tx/${escapedHash}`;
    case DataSourceCode.Mantra:
      return `https://www.mintscan.io/mantra/tx/${escapedHash}`;
    case DataSourceCode.Medibloc:
      return `https://www.mintscan.io/medibloc/tx/${escapedHash}`;
    case DataSourceCode.Milkyway:
      return `https://www.mintscan.io/milkyway/tx/${escapedHash}`;
    case DataSourceCode.Neutron:
      return `https://www.mintscan.io/neutron/tx/${escapedHash}`;
    case DataSourceCode.Noble:
      return `https://www.mintscan.io/noble/tx/${escapedHash}`;
    case DataSourceCode.Omniflix:
      return `https://www.mintscan.io/omniflix/tx/${escapedHash}`;
    case DataSourceCode.OnomyProtocol:
      return `https://www.mintscan.io/onomy-protocol/tx/${escapedHash}`;
    case DataSourceCode.Osmosis:
      return `https://www.mintscan.io/osmosis/tx/${escapedHash}`;
    case DataSourceCode.Persistence:
      return `https://www.mintscan.io/persistence/tx/${escapedHash}`;
    case DataSourceCode.Quasar:
      return `https://www.mintscan.io/quasar/tx/${escapedHash}`;
    case DataSourceCode.Secret:
      return `https://www.mintscan.io/secret/tx/${escapedHash}`;
    case DataSourceCode.Sei:
      return `https://www.mintscan.io/sei/tx/${escapedHash}`;
    case DataSourceCode.Selfchain:
      return `https://www.mintscan.io/selfchain/tx/${escapedHash}`;
    case DataSourceCode.Sentinel:
      return `https://www.mintscan.io/sentinel/tx/${escapedHash}`;
    case DataSourceCode.Shentu:
      return `https://www.mintscan.io/shentu/tx/${escapedHash}`;
    case DataSourceCode.Sommelier:
      return `https://www.mintscan.io/sommelier/tx/${escapedHash}`;
    case DataSourceCode.StaFi:
      return `https://www.mintscan.io/stafi/tx/${escapedHash}`;
    case DataSourceCode.Stargaze:
      return `https://www.mintscan.io/stargaze/tx/${escapedHash}`;
    case DataSourceCode.Stride:
      return `https://www.mintscan.io/stride/tx/${escapedHash}`;
    case DataSourceCode.Xion:
      return `https://www.mintscan.io/xion/tx/${escapedHash}`;
    case DataSourceCode.Xpla:
      return `https://www.mintscan.io/xpla/tx/${escapedHash}`;
    case DataSourceCode.Zetachain:
      return `https://www.mintscan.io/zeta/tx/${escapedHash}`;

    case DataSourceCode.KyveNetwork:
      return `https://viewblock.io/kyve/tx/${escapedHash}`;
    case DataSourceCode.Iotex:
      return `https://iotexscan.io/tx/${escapedHash}`;
    case DataSourceCode.AvalancheXChain:
      return `https://subnets.avax.network/x-chain/tx/${escapedHash}`;
    case DataSourceCode.Stacks:
      return `https://explorer.hiro.so/txid/${escapedHash}?chain=mainnet`;
    case DataSourceCode.MultiversX:
      return `https://explorer.multiversx.com/transactions/${escapedHash}`;
    case DataSourceCode.HederaHashgraph:
      return `https://hashscan.io/mainnet/transaction/${escapedHash}`;
    case DataSourceCode.Solana:
      return `https://solscan.io/tx/${escapedHash}`;
    case DataSourceCode.Symbol:
      return `https://symbol.fyi/transactions/${escapedHash}`;
    case DataSourceCode.InternetComputer:
      return `https://www.icpexplorer.org/#/tx/${escapedHash}`;
    case DataSourceCode.MinaProtocol:
      return `https://minaexplorer.com/transaction/${escapedHash}`;
    case DataSourceCode.CasperNetwork:
      return `https://cspr.live/deploy/${escapedHash}`;
    case DataSourceCode.Stellar:
      return `https://stellar.expert/explorer/public/tx/${escapedHash}`;
    case DataSourceCode.Tron:
      return `https://tronscan.org/#/transaction/${escapedHash}`;
    case DataSourceCode.Thorchain:
      return `https://runescan.io/tx/${escapedHash}`;
    case DataSourceCode.Ton:
      return `https://tonscan.org/tx/${escapedHash}`;
    case DataSourceCode.Elastos:
      return `https://ela.elastos.io/tx/${escapedHash}`;
    case DataSourceCode.BnbBeaconChain:
      // requires uppercase even though the hashes are case-insensitive
      return `https://explorer.bnbchain.org/tx/${escapedHash.toUpperCase()}`;
    case DataSourceCode.WavesBlockchain:
      return `https://wavesexplorer.com/transactions/${escapedHash}`;
    case DataSourceCode.ICON:
      return `https://tracker.icon.community/transaction/${escapedHash}`;
    case DataSourceCode.NearProtocol:
      return `https://nearblocks.io/txns/${escapedHash}`;
    case DataSourceCode.Theta:
      return `https://explorer.thetatoken.org/txs/${escapedHash}`;
    case DataSourceCode.Flow:
      return `https://www.flowscan.io/tx/${escapedHash}`;
    case DataSourceCode.Bitcoin:
      return `https://bitaps.com/${escapedHash}`;
    case DataSourceCode.Zcash:
      return `https://mainnet.zcashexplorer.app/transactions/${escapedHash}`;
    case DataSourceCode.Groestlcoin:
      return `https://groestlsight.groestlcoin.org/tx/${escapedHash}`;
    case DataSourceCode.DigiByte:
      return `https://digiexplorer.info/tx/${escapedHash}`;
    case DataSourceCode.BitcoinSv:
      return `https://whatsonchain.com/tx/${escapedHash}`;
    case DataSourceCode.Litecoin:
      return `https://litecoinblockexplorer.net/tx/${escapedHash}`;
    case DataSourceCode.Kaspa:
      return `https://kaspa.stream/transactions/${escapedHash}`;
    case DataSourceCode.Linea:
      return `https://lineascan.build/tx/${escapedHash}`;
    case DataSourceCode.EthereumPow:
      return `https://www.oklink.com/ethw/tx/${escapedHash}`;
    case DataSourceCode.Harmony:
      return `https://explorer.harmony.one/tx/${escapedHash}`;
    case DataSourceCode.SeiNetwork:
      return `https://www.seiscan.app/pacific-1/txs/${escapedHash}`;
    case DataSourceCode.Vechain:
      return `https://explore.vechain.org/transactions/${escapedHash}`;
    case DataSourceCode.Nano:
      return `https://nanexplorer.com/all/block/${escapedHash}`;
    case DataSourceCode.Eos:
      return `https://eosflare.io/tx/${escapedHash}`;
    case DataSourceCode.Zilliqa:
      return `https://viewblock.io/zilliqa/tx/${escapedHash}`;
    case DataSourceCode.NeoLegacy:
      return `https://neo2.neotube.io/transaction/${escapedHash}`;
    case DataSourceCode.NeoN3:
      return `https://neotube.io/transaction/${escapedHash}`;
    case DataSourceCode.TerraClassic:
      return `https://finder.terra.money/classic/tx/${escapedHash}`;
    case DataSourceCode.Terra:
      return `https://finder.terra.money/mainnet/tx/${escapedHash}`;
    case DataSourceCode.EnergyWeb:
      return `https://explorer.energyweb.org/tx/${escapedHash}`;
    case DataSourceCode.Wax:
      return `https://waxblock.io/transaction/${escapedHash}`;
    case DataSourceCode.Beldex:
      return `https://explorer.beldex.io/tx/${escapedHash}`;
    case DataSourceCode.Horizon:
      return `https://explorer.horizen.io/tx/${escapedHash}`;
    case DataSourceCode.Wanchain:
      return `https://wanscan.org/tx/${escapedHash}`;
    case DataSourceCode.Ronin:
      return `https://app.roninchain.com/tx/${escapedHash}`;
    case DataSourceCode.Dymension:
      return `https://dymension.explorers.guru/transaction/${escapedHash}`;
    case DataSourceCode.PiNetwork:
      return `https://blockexplorer.minepi.com/mainnet/transactions/${escapedHash}`;
    case DataSourceCode.Firo:
      return `https://explorer.firo.org/tx/${escapedHash}`;
    case DataSourceCode.Ravencoin:
      return `https://blockbook.ravencoin.org/tx/${escapedHash}`;
    case DataSourceCode.GoChain:
      return `https://explorer.gochain.io/tx/${escapedHash}`;
    case DataSourceCode.Core:
      return `https://scan.coredao.org/tx/${escapedHash}`;
    case DataSourceCode.Aleo:
      return `https://aleoscan.io/transaction?id=${escapedHash}`;
    case DataSourceCode.IotaLegacy:
      // only transactions prior to milestone 4984942
      return `https://archive.explorer.iota.org/legacy-mainnet/transaction/${escapedHash}`;
    case DataSourceCode.IotaChrysalis:
      // only transactions prior to milestone 7669900
      return `https://archive.explorer.iota.org/chrysalis-mainnet/message/${escapedHash}`;
    case DataSourceCode.IotaStardust:
      // only transactions prior to milestone 17011901
      return `https://archive.explorer.iota.org/mainnet/transaction/${escapedHash}`;
    case DataSourceCode.IotaRebased:
      return `https://explorer.iota.org/txblock/${escapedHash}`;
    case DataSourceCode.IronFish:
      return `https://explorer.ironfish.network/transaction/${escapedHash}`;

    // Polkadot ecosystem
    case DataSourceCode.Polkadot:
      return `https://polkadot.subscan.io/extrinsic/${escapedHash}`;
    case DataSourceCode.Kusama:
      return `https://kusama.subscan.io/extrinsic/${escapedHash}`;
    case DataSourceCode.Acala:
      return `https://acala.subscan.io/extrinsic/${escapedHash}`;
    case DataSourceCode.Astar:
      return `https://astar.subscan.io/extrinsic/${escapedHash}`;
    case DataSourceCode.Enjin:
      return `https://enjin.subscan.io/extrinsic/${escapedHash}`;
    case DataSourceCode.VaraNetwork:
      return `https://vara.subscan.io/extrinsic/${escapedHash}`;

    // Blockchair
    case DataSourceCode.Dogecoin:
      return `https://blockchair.com/dogecoin/transaction/${escapedHash}`;
    case DataSourceCode.BitcoinCash:
      return `https://blockchair.com/bitcoin-cash/transaction/${escapedHash}`;
    case DataSourceCode.ECash:
      return `https://blockchair.com/ecash/transaction/${escapedHash}`;
    case DataSourceCode.Dash:
      return `https://blockchair.com/dash/transaction/${escapedHash}`;

    // Etherscan
    case DataSourceCode.Chiliz:
      return `https://scan.chiliz.com/tx/${escapedHash}`;
    case DataSourceCode.XRP:
      return `https://xrpscan.com/tx/${escapedHash}`;
    case DataSourceCode.Beam:
      return `https://subnets.avax.network/beam/tx/${escapedHash}`;
    case DataSourceCode.Scroll:
      return `https://scrollscan.com/tx/${escapedHash}`;
    case DataSourceCode.Metis:
      return `https://andromeda-explorer.metis.io/tx/${escapedHash}`;
    case DataSourceCode.Taiko:
      return `https://taikoscan.io/tx/${escapedHash}`;
    case DataSourceCode.Fraxtal:
      return `https://fraxscan.com/tx/${escapedHash}`;
    case DataSourceCode.Zora:
      return `https://explorer.zora.energy/tx/${escapedHash}`;
    case DataSourceCode.Cyber:
      return `https://cyberscan.co/tx/${escapedHash}`;
    case DataSourceCode.DefiKingdoms:
      return `https://subnets.avax.network/defi-kingdoms/tx/${escapedHash}`;
    case DataSourceCode.Mint:
      return `https://explorer.mintchain.io/tx/${escapedHash}`;
    case DataSourceCode.Flare:
      return `https://flare-explorer.flare.network/tx/${escapedHash}`;
    case DataSourceCode.Songbird:
      return `https://songbird-explorer.flare.network/tx/${escapedHash}`;
    case DataSourceCode.Avalanche:
      return `https://subnets.avax.network/c-chain/tx/${escapedHash}`;
    case DataSourceCode.Ethereum:
      return `https://etherscan.io/tx/${escapedHash}`;
    case DataSourceCode.BinanceSmartChain:
      return `https://bscscan.com/tx/${escapedHash}`;
    case DataSourceCode.Celo:
      return `https://celoscan.io/tx/${escapedHash}`;
    case DataSourceCode.Polygon:
      return `https://polygonscan.com/tx/${escapedHash}`;
    case DataSourceCode.Fantom:
      return `https://explorer.fantom.network/transactions/${escapedHash}`;
    case DataSourceCode.Cronos:
      return `https://explorer.cronos.org/tx/${escapedHash}`;
    case DataSourceCode.Moonbeam:
      return `https://moonscan.io/tx/${escapedHash}`;
    case DataSourceCode.Arbitrum:
      return `https://arbiscan.io/tx/${escapedHash}`;
    case DataSourceCode.Optimism:
      return `https://optimistic.etherscan.io/tx/${escapedHash}`;
    case DataSourceCode.Base:
      return `https://basescan.org/tx/${escapedHash}`;
    case DataSourceCode.Blast:
      return `https://blastscan.io/tx/${escapedHash}`;
    case DataSourceCode.Mode:
      return `https://explorer.mode.network/tx/${escapedHash}`;
    case DataSourceCode.Mantle:
      return `https://explorer.mantle.xyz/tx/${escapedHash}`;
    case DataSourceCode.ZkSyncEra:
      return `https://explorer.zksync.io/tx/${escapedHash}`;
    case DataSourceCode.Gnosis:
      return `https://gnosisscan.io/tx/${escapedHash}`;
    case DataSourceCode.Abstract:
      return `https://abscan.org/tx/${escapedHash}`;
    case DataSourceCode.ApeChain:
      return `https://apescan.io/tx/${escapedHash}`;
    case DataSourceCode.ArbitrumNova:
      return `https://nova.arbiscan.io/tx/${escapedHash}`;
    case DataSourceCode.Berachain:
      return `https://berascan.com/tx/${escapedHash}`;
    case DataSourceCode.BitTorrent:
      return `https://bttcscan.com/tx/${escapedHash}`;
    case DataSourceCode.Moonriver:
      return `https://moonriver.moonscan.io/tx/${escapedHash}`;
    case DataSourceCode.PolygonZkEvm:
      return `https://www.oklink.com/polygon-zkevm/tx/${escapedHash}`;
    case DataSourceCode.Sonic:
      return `https://sonicscan.org/tx/${escapedHash}`;
    case DataSourceCode.Unichain:
      return `https://uniscan.xyz/tx/${escapedHash}`;
    case DataSourceCode.WemixNetwork:
      return `https://scan.wemix.com/tx/${escapedHash}`;
    case DataSourceCode.WorldChain:
      return `https://worldscan.org/tx/${escapedHash}`;
    case DataSourceCode.XDC:
      return `https://xdcscan.com/tx/${escapedHash}`;
    case DataSourceCode.opBNB:
      return `https://opbnb.bscscan.com/tx/${escapedHash}`;
    case DataSourceCode.Xai:
      return `https://xaiscan.io/tx/${escapedHash}`;
    case DataSourceCode.KardiaChain:
      return `https://explorer.kardiachain.io/tx/${escapedHash}`;
    case DataSourceCode.Swellchain:
      return `https://swellchainscan.io/tx/${escapedHash}`;
    case DataSourceCode.Sophon:
      return `https://explorer.sophon.xyz/tx/${escapedHash}`;
    case DataSourceCode.Memecore:
      return `https://memecorescan.io/tx/${escapedHash}`;
    case DataSourceCode.HyperEvm:
      return `https://hyperevmscan.io/tx/${escapedHash}`;
    case DataSourceCode.Monad:
      return `https://monadscan.com/tx/${escapedHash}`;
    case DataSourceCode.Katana:
      return `https://katanascan.com/tx/${escapedHash}`;
    case DataSourceCode.SeiEvm:
      return `https://seiscan.io/tx/${escapedHash}`;
    case DataSourceCode.Plasma:
      return `https://plasmascan.to/tx/${escapedHash}`;
    case DataSourceCode.MegaEth:
      return `https://mega.etherscan.io/tx/${escapedHash}`;

    // RouteScan
    case DataSourceCode.Kroma:
      return `https://kroscan.io/tx/${escapedHash}`;
    case DataSourceCode.Wyzth:
      return `https://303.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Morph:
      return `https://2818.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.RedbellyNetwork:
      return `https://151.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Nibiru:
      return `https://6900.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Dexalot:
      return `https://432204.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Lamina1:
      return `https://10849.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Pulsar:
      return `https://357.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Ink:
      return `https://57073.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Lisk:
      return `https://1135.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Donatuz:
      return `https://42026.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Bitrock:
      return `https://7171.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Ancient8:
      return `https://888888888.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Polynomial:
      return `https://polynomialscan.io/tx/${escapedHash}`;
    case DataSourceCode.Superseed:
      return `https://5330.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Funki:
      return `https://funkiscan.io/tx/${escapedHash}`;
    case DataSourceCode.Ethernity:
      return `https://ernscan.io/tx/${escapedHash}`;
    case DataSourceCode.UPTN:
      return `https://6119.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.SwanChain:
      return `https://swanscan.io/tx/${escapedHash}`;
    case DataSourceCode.Playdapp:
      return `https://504441.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.QChain:
      return `https://12150.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Corn:
      return `https://21000000.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.HighOctane:
      return `https://1853.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.GUNZ:
      return `https://43419.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Tiltyard:
      return `https://710420.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Omni:
      return `https://omniscan.network/tx/${escapedHash}`;
    case DataSourceCode.StraitsX:
      return `https://5566.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Coqnet:
      return `https://42069.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Teleporter1:
      return `https://62707.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Teleporter2:
      return `https://70953.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.DOS:
      return `https://7979.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Orderly:
      return `https://291.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Kinto:
      return `https://kintoscan.io/tx/${escapedHash}`;
    case DataSourceCode.Playa3ull:
      return `https://explorer.playa3ull.games/tx/${escapedHash}`;
    case DataSourceCode.Shrapnel:
      return `https://2044.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.Numbers:
      return `https://10507.routescan.io/tx/${escapedHash}`;
    case DataSourceCode.XANAChain:
      return `https://8888.routescan.io/tx/${escapedHash}`;

    // Blockscout
    case DataSourceCode.EthereumClassic:
      return `https://etc.blockscout.com/tx/${escapedHash}`;
    case DataSourceCode.PulseChain:
      return `https://scan.9mm.pro/tx/${escapedHash}`;
    case DataSourceCode.StratisEvm:
      return `https://explorer.stratisevm.com/tx/${escapedHash}`;
    case DataSourceCode.IotaEvm:
      return `https://explorer.evm.iota.org/tx/${escapedHash}`;
    case DataSourceCode.LoopNetwork:
      return `https://explorer.mainnetloop.com/tx/${escapedHash}`;
    case DataSourceCode.NeoX:
      return `https://xexplorer.neo.org/tx/${escapedHash}`;
    case DataSourceCode.Electroneum:
      return `https://blockexplorer.electroneum.com/tx/${escapedHash}`;
    case DataSourceCode.Degen:
      return `https://explorer.degen.tips/tx/${escapedHash}`;
    case DataSourceCode.XrplEvm:
      return `https://explorer.testnet.xrplevm.org/tx/${escapedHash}`;
    case DataSourceCode.Lumia:
      return `https://prism-blockscout.eu-north-2.gateway.fm/tx/${escapedHash}`;
    case DataSourceCode.ElastosSmartContractChain:
      return `https://eth.elastos.io/tx/${escapedHash}`;
    case DataSourceCode.FlowEvm:
      return `https://evm.flowscan.io/tx/${escapedHash}`;
    case DataSourceCode.Ultra:
      return `https://explorer.mainnet.ultra.io/tx/${escapedHash}`;
    case DataSourceCode.KuCoinCommunityChain:
      return `https://scan.kcc.io/tx/${escapedHash}`;
    case DataSourceCode.LayerK:
      return `https://explorer.layerk.com/tx/${escapedHash}`;

    case DataSourceCode.Qoin:
      // the user must be logged in to a private portal to go to the link,
      // so it's better to not show.
      return undefined;
    default:
      return undefined;
  }
};
